import type { GameState } from '../state';
import { D, add, sub, mul, min, lte, gt, type Decimal } from '../math/num';
import { chance } from '../math/rng';
import { evaluateGraphV2 } from '../graph/evaluatorV2';
import { getFrame, frameMaxHp, frameDamageMult } from '../data/frames';
import { spawnEnemy, BOSS_EVERY, MAX_ENEMIES, waveEnemyCount, type ActiveEnemy } from './spawn';
import { rollDrop } from './loot';
import type { EmitFn } from './events';
import type { EffectiveAttack } from '../data/parts/partTypes';
import { coreBonuses } from './meta';
import { droneScrapBonus } from './drones';
import { winOverlordFight, OVERLORD_ENEMY_ID } from './overlords';
import { petBonuses, rollPetDrop } from './pets';
import { railScrapBonus } from './rails';
import { takeoverComponentBonus } from './takeover';
import { districtBonuses } from './districts';
import { harvestCoreBoost } from './harvest';
import { boostConductance } from './grid';
import { enrageMult } from './enrage';

export { MAX_ENEMIES, waveEnemyCount };

/** Active-play tuning. */
export const MAX_EFFORT_MULT = 3;
export const MAX_EFFORT_DURATION_MS = 5_000;
export const MAX_EFFORT_CD_MS = 30_000;
/** Full Send: a 1s global cooldown so it can't be spammed (which let an AOE
 *  build clear many waves per frame and hang the game), and a 3x payoff so a
 *  well-timed click is worth more than the old infinite-spam ever was. */
export const FULL_SEND_CD_MS = 1_000;
export const FULL_SEND_MULT = 3;

/** Losing shows a reboot countdown, then waves resume one wave back. */
export const DEFEAT_PAUSE_MS = 5_000;

const MAX_FIRES_PER_STEP = 2_000;

/** Start wave of the 10-wave segment a wave belongs to (the retreat checkpoint). */
export function segmentStart(wave: number): number {
  return Math.floor((wave - 1) / BOSS_EVERY) * BOSS_EVERY + 1;
}

function frameTier(state: GameState): number {
  return getFrame(state.frame.defId).tier;
}

function spawnHorde(state: GameState, emit?: EmitFn): void {
  state.combat.enrageMs = 0; // a fresh horde starts calm
  const count = waveEnemyCount(state.combat.wave);
  const tier = frameTier(state);
  for (let i = 0; i < count; i++) {
    const id = `x${state.combat.nextEnemyId++}`;
    const e = spawnEnemy(state.combat.wave, tier, state.rng, id);
    state.combat.enemies.push(e);
    emit?.({ kind: 'spawn', targetId: id });
  }
}

function ensureHorde(state: GameState, emit?: EmitFn): void {
  if (state.combat.enemies.length === 0) spawnHorde(state, emit);
}

/** Effective attacks + the global damage multiplier (frame level × Maximum Effort).
 *  scrapMult folds in the drone field bonus (idle drones auto-loot); equipped
 *  pets layer low-ceiling multipliers on top (the Pets expansion system). */
export function effectiveAttacks(state: GameState): { attacks: EffectiveAttack[]; globalMult: Decimal; dropRateMult: number; scrapMult: Decimal; componentMult: Decimal; regenFracBonus: number; regenMult: number } {
  const ev = evaluateGraphV2(state.mind, state.inventory.parts, frameTier(state), harvestCoreBoost(state), boostConductance(state));
  const bonus = coreBonuses(state);
  const pets = petBonuses(state);
  const city = districtBonuses(state);
  let mult = mul(mul(frameDamageMult(state.frame.level), bonus.damageMult), pets.damageMult);
  if (state.combat.maxEffortUntil > state.elapsedMs) mult = mul(mult, MAX_EFFORT_MULT);
  return {
    attacks: ev.global.overloaded ? [] : ev.attacks,
    globalMult: ev.global.overloaded ? D(0) : mult,
    dropRateMult: ev.global.dropRateMult.toNumber() * bonus.dropMult * pets.dropRateMult,
    scrapMult: mul(mul(mul(mul(mul(ev.global.scrapMult, bonus.scrapMult), droneScrapBonus(state)), pets.scrapMult), railScrapBonus(state)), D(1 + city.scrap)),
    componentMult: mul(mul(mul(mul(ev.global.componentMult, bonus.componentMult), pets.componentMult), takeoverComponentBonus(state)), D(1 + city.components)),
    regenFracBonus: ev.global.regenAdd,
    regenMult: pets.regenMult,
  };
}

/** DPS from an already-evaluated attack set (expected value, crit-weighted).
 *  stepCombat uses this so the graph evaluates ONCE per tick, not twice. */
export function dpsOf(attacks: EffectiveAttack[], globalMult: Decimal): Decimal {
  let dps = D(0);
  for (const a of attacks) {
    const perHit = mul(a.damage, globalMult);
    const critFactor = 1 + a.critChance * (a.critMult.toNumber() - 1);
    const hitsPerSec = 1000 / a.cooldownMs;
    dps = add(dps, mul(perHit, D(critFactor * hitsPerSec)));
  }
  return dps;
}

/** Display/test DPS estimate. */
export function currentDps(state: GameState): Decimal {
  const { attacks, globalMult } = effectiveAttacks(state);
  return dpsOf(attacks, globalMult);
}

function frontmost(enemies: ActiveEnemy[]): ActiveEnemy | undefined {
  return enemies[0];
}

/** Apply one hit to a target; returns true if it died. */
function applyHit(state: GameState, atk: EffectiveAttack, target: ActiveEnemy, globalMult: Decimal, emit?: EmitFn): void {
  const crit = chance(state.rng, atk.critChance);
  let dmg = mul(atk.damage, globalMult);
  if (crit) dmg = mul(dmg, atk.critMult);
  const eff = target.resist[atk.damageType] ?? 1;
  dmg = mul(dmg, eff);
  target.hp = sub(target.hp, dmg);
  emit?.({ kind: 'hit', nodeId: atk.sourceNodeId, attackName: atk.name, targetId: target.id, amount: dmg, crit, damageType: atk.damageType, attackKind: atk.kind, eff });
}

/** Fire one attack at its target(s) once (no cooldown bookkeeping). */
function fireOnce(state: GameState, atk: EffectiveAttack, globalMult: Decimal, emit?: EmitFn): void {
  const enemies = state.combat.enemies;
  if (enemies.length === 0) return;
  if (atk.kind === 'aoe') {
    for (const e of enemies) applyHit(state, atk, e, globalMult, emit);
  } else {
    const target = frontmost(enemies);
    if (target) applyHit(state, atk, target, globalMult, emit);
  }
}

function rewardKill(state: GameState, enemy: ActiveEnemy, scrapMult: Decimal, componentMult: Decimal, dropRateMult: number, emit?: EmitFn): void {
  state.wallet.scrap = add(state.wallet.scrap, mul(enemy.scrapReward, scrapMult));
  state.meta.lifetimeScrap = add(state.meta.lifetimeScrap, mul(enemy.scrapReward, scrapMult));
  if (gt(enemy.componentReward, D(0))) {
    state.wallet.components = add(state.wallet.components, mul(enemy.componentReward, componentMult));
  }
  state.combat.killsThisRun += 1;
  state.stats.totalKills += 1;

  // Rare part drop (the loot loop that feeds the Mind).
  const drop = rollDrop(state.combat.wave, frameTier(state), enemy.isBoss, state.inventory, state.rng, dropRateMult);
  if (drop.instance) {
    if (!state.meta.unlocks.salvage) state.meta.unlocks.salvage = true;
    emit?.({ kind: 'drop', instanceId: drop.instance.instanceId, rarity: drop.instance.rarity });
  }

  // Rarer still: the mob itself, pocket-sized (Pets, once DJ Leggs falls).
  rollPetDrop(state, enemy, emit);
}

function advanceWave(state: GameState, emit?: EmitFn): void {
  emit?.({ kind: 'waveClear', wave: state.combat.wave });
  // Auto-advance climbs to the next wave; "hold" keeps the same wave and just
  // respawns it, so the player can farm a wave in place.
  if (state.combat.autoAdvance) {
    state.combat.wave += 1;
    if (state.combat.wave > state.combat.highestWave) state.combat.highestWave = state.combat.wave;
    if (state.combat.wave > state.stats.bestWave) state.stats.bestWave = state.combat.wave;
    state.combat.checkpoint = segmentStart(state.combat.wave);
  }
  spawnHorde(state, emit);
}

/** Going down starts a reboot countdown; waves resume ONE wave back when it
 *  lifts (a soft tax, not a checkpoint yank). Also ends any overlord duel. */
function defeatFrame(state: GameState, maxHp: Decimal, emit?: EmitFn): void {
  state.frame.hp = maxHp;
  state.combat.wave = Math.max(1, state.combat.wave - 1);
  state.combat.checkpoint = segmentStart(state.combat.wave);
  state.combat.enemies = [];
  state.combat.overlordId = null;
  state.combat.enrageMs = 0; // the encounter ended; the next one starts calm
  state.combat.pauseKind = 'defeat';
  state.combat.pauseUntilMs = state.elapsedMs + DEFEAT_PAUSE_MS;
  emit?.({ kind: 'frameReboot' });
}

/** Reap dead enemies, paying rewards and rolling drops. Advances the wave when
 *  cleared. A fallen overlord routes to the victory path instead (its rewards
 *  are first-kill-only, paid there). */
function reap(state: GameState, scrapMult: Decimal, componentMult: Decimal, dropRateMult: number, emit?: EmitFn): void {
  const survivors: ActiveEnemy[] = [];
  let overlordDown = false;
  for (const e of state.combat.enemies) {
    if (lte(e.hp, D(0))) {
      if (state.combat.overlordId && e.id === OVERLORD_ENEMY_ID) {
        overlordDown = true;
        emit?.({ kind: 'death', targetId: e.id, name: e.name });
        continue;
      }
      rewardKill(state, e, scrapMult, componentMult, dropRateMult, emit);
      emit?.({ kind: 'death', targetId: e.id, name: e.name });
    } else {
      survivors.push(e);
    }
  }
  state.combat.enemies = survivors;
  if (overlordDown) {
    winOverlordFight(state, emit);
    return;
  }
  if (survivors.length === 0) advanceWave(state, emit);
}

export function stepCombat(state: GameState, dtMs: number, emit?: EmitFn): void {
  const dtSec = dtMs / 1000;
  const frameDef = getFrame(state.frame.defId);
  const maxHp = frameMaxHp(frameDef, state.frame.level);

  // Arena pause (reboot countdown / post-overlord breather): combat holds.
  if (state.combat.pauseKind) {
    if (state.elapsedMs < state.combat.pauseUntilMs) {
      state.combat.currentDps = currentDps(state);
      return;
    }
    state.combat.pauseKind = null;
  }

  ensureHorde(state, emit);
  const { attacks, globalMult, dropRateMult, scrapMult, componentMult, regenFracBonus, regenMult } = effectiveAttacks(state);
  state.combat.currentDps = dpsOf(attacks, globalMult);

  // 1) Frame takes damage from the whole living horde, then regenerates.
  // Natural regen is deliberately slim; healing parts (regen utilities) are the
  // real sustain choice. Actual healing is emitted so the log and floaters show it.
  // Enrage: the longer THIS encounter drags, the harder everything hits — you
  // cannot face-tank a fight you cannot win (sustain buys time, not stalemate).
  state.combat.enrageMs += dtMs;
  let incoming = D(0);
  for (const e of state.combat.enemies) incoming = add(incoming, mul(e.dmgPerSec, enrageMult(state, e)));
  const tookDamage = gt(incoming, D(0));
  state.frame.hp = sub(state.frame.hp, mul(incoming, D(dtSec)));
  const hpBeforeRegen = state.frame.hp;
  state.frame.hp = min(add(state.frame.hp, mul(maxHp, D((frameDef.regenFrac + regenFracBonus) * regenMult * dtSec))), maxHp);
  const healed = sub(state.frame.hp, hpBeforeRegen);
  if (tookDamage && emit) emit({ kind: 'frameDamaged', amount: mul(incoming, D(dtSec)), sourceName: state.combat.enemies[0]?.name ?? 'the horde' });
  if (gt(healed, D(0)) && emit) emit({ kind: 'frameHealed', amount: healed });
  if (lte(state.frame.hp, D(0))) {
    defeatFrame(state, maxHp, emit);
    return;
  }

  // 2) Run each attack's cooldown; fire while ready (offline-safe via cap).
  const timers = state.combat.attackTimers;
  const liveIds = new Set(attacks.map((a) => a.sourceNodeId));
  for (const key of Object.keys(timers)) if (!liveIds.has(key)) delete timers[key]; // prune stale

  for (const atk of attacks) {
    let t = (timers[atk.sourceNodeId] ?? 0) - dtMs;
    let fires = 0;
    while (t <= 0 && fires < MAX_FIRES_PER_STEP) {
      if (state.combat.enemies.length === 0) {
        t = 0;
        break;
      }
      fireOnce(state, atk, globalMult, emit);
      reap(state, scrapMult, componentMult, dropRateMult, emit);
      t += atk.cooldownMs;
      fires++;
    }
    timers[atk.sourceNodeId] = t;
  }
}

/** Full Send: fire every attack at 3x immediately (active optimization,
 *  never required). Rate-limited to once per FULL_SEND_CD_MS — without the
 *  gate, holding the button fired many bursts per frame and an AOE build cleared
 *  waves faster than the tick could reconcile, hanging the game. */
export function fullSendBurst(state: GameState, emit?: EmitFn): number {
  if (state.combat.pauseKind) return 0;
  if (state.elapsedMs < state.combat.fullSendCdUntil) return 0;
  state.combat.fullSendCdUntil = state.elapsedMs + FULL_SEND_CD_MS;
  const { attacks, globalMult, dropRateMult, scrapMult, componentMult } = effectiveAttacks(state);
  ensureHorde(state, emit);
  const burstMult = mul(globalMult, D(FULL_SEND_MULT));
  let fired = 0;
  for (const atk of attacks) {
    fireOnce(state, atk, burstMult, emit);
    fired++;
  }
  reap(state, scrapMult, componentMult, dropRateMult, emit);
  return fired;
}
